import { useLanguage } from '@/app/contexts/language-context';
import { translations } from '@/app/locales';

/**
 * Hook to use bundled translations based on current language
 * Translations are imported at compile-time (0ms access from memory)
 * Usage: const { t } = useTranslation(); then use t('key_name')
 */
export const useTranslation = () => {
    const { language } = useLanguage();

    /**
     * Get translation by key
     * @param key - Translation key (e.g., 'display_language')
     * @param defaultValue - Default value if key not found (optional)
     * @returns Translated string or default value or key name
     */
    const t = (key: string, defaultValue?: string): string => {
        // Map language context code to translation object key
        const languageMap: { [key: string]: 'en' | 'ja' | 'ko' } = {
            'en': 'en',
            'ja': 'ja',
            'ko': 'ko',
        };

        const translationLanguage = languageMap[language] || 'ko';
        const currentTranslations = translations[translationLanguage];
        return currentTranslations[key] || defaultValue || key;
    };

    // Note: loading is no longer needed as translations are bundled
    // Kept for backwards compatibility if needed
    return { t, language, loading: false };
};
